/**
 * Projects Store - Gestione completa modulo progetti
 *
 * Sotto-moduli:
 * - Progetti (prj-)
 * - Tasks (tsk-)
 * - Time Entries (tme-)
 * - Milestones (mst-)
 * - Dashboard
 *
 * Segue architettura megapack con ID tipizzati.
 * Usa Repository Pattern per database-agnostic persistence.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { genId } from '../domain/idUtils';
import { createZustandStorage } from '../storage/adapter';
import { getRepository } from '../repository';
import type { IDataRepository } from '../repository';
import { hasPermission, Permission } from '../auth/permissions';
import { graphqlProjectsRepository } from '../repositories/GraphQLProjectsRepository';
import type {
  Project,
  ProjectSummary,
  ProjectStatus,
  ProjectPriority,
  ProjectDashboard,
  Task,
  TaskStatus,
  TaskPriority,
  TaskPrivacy,
  TaskReminder,
  TaskRepetition,
  TimeEntry,
  Milestone,
  ProjectFile,
  RiskLevel,
  SidebarMenu,
} from '../domain/models';
import {
  getOrCreateProjectFolder,
  syncFromDrive,
  deleteFromDrive,
  driveFileToProjectFile,
  getProjectSyncStatus,
  type SyncStatus,
} from '../services/projectDriveSync';
import { uploadDriveFile } from '../services/googleDriveClient';

// =============================================================================
// SIDEBAR MENU CONFIG
// =============================================================================

const PROJECTS_SIDEBAR_MENU: SidebarMenu = {
  sections: [
    {
      title: 'Stato',
      items: [
        { key: 'all', label: 'Tutti', hint: 'Tutti i progetti' },
        { key: 'attivo', label: 'Attivi', hint: 'Progetti attivi' },
        { key: 'archiviato', label: 'Archiviati', hint: 'Progetti archiviati' },
      ],
    },
    {
      title: 'Salute',
      items: [
        { key: 'da-iniziare', label: 'Da iniziare', hint: 'Non ancora iniziati' },
        { key: 'in-corso', label: 'In corso', hint: 'Lavori in corso' },
        { key: 'pericolo', label: 'Pericolo', hint: 'Attenzione richiesta' },
        { key: 'ritardo', label: 'Ritardo', hint: 'In ritardo sulla tabella' },
      ],
    },
    {
      title: 'Priorità',
      items: [
        { key: 'urgent', label: 'Urgenti', hint: 'Priorità massima' },
        { key: 'high', label: 'Alta priorità' },
      ],
    },
  ],
};

// =============================================================================
// REPOSITORY INSTANCE
// =============================================================================

let projectsRepo: IDataRepository;
let tasksRepo: IDataRepository;
let timeEntriesRepo: IDataRepository;
let milestonesRepo: IDataRepository;
let filesRepo: IDataRepository;

// Initialize repositories
async function initRepositories() {
  projectsRepo = await getRepository();
  tasksRepo = await getRepository();
  timeEntriesRepo = await getRepository();
  milestonesRepo = await getRepository();
  filesRepo = await getRepository();
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// =============================================================================
// PROJECT-ID RESOLUTION (workaround for backend `id: ""` on read paths)
//
// TODO(issue:backend-id-serialization): the live backend returns `id:
// ""` for every project on the read side. The repository mints a
// deterministic `synth-<slug>` for navigation and stores the real uuid
// on `project.realId` (discovered by matching the title of
// `project(id: <real>)` against the foreign keys on tasks/milestones/
// time entries). Sub-entities are fetched with the realId, so their
// `projectId` field is the real uuid. Callers usually hold the synth-
// id (from the URL) when they ask the store for sub-entities, so the
// naive equality check `entity.projectId === projectId` would always
// return empty. This helper looks up the project's realId and matches
// against both ids so the store just works.
// =============================================================================

type SubEntity = 'tasks' | 'milestones' | 'timeEntries';

type SubEntityRow = { projectId: string };

interface SubEntityHost {
  projects: Array<{ id: string; realId?: string }>;
  tasks: SubEntityRow[];
  milestones: SubEntityRow[];
  timeEntries: SubEntityRow[];
}

function resolveProjectSubEntities<K extends SubEntity>(
  state: SubEntityHost,
  projectId: string,
  field: K
): SubEntityHost[K] {
  // The synth- and real ids are different strings: the input is the
  // synth- id from the URL, the entity rows store the real uuid. Match
  // by realId when we have it, fall back to the input id for projects
  // whose realId is unknown (no sub-entities ever fetched, or the
  // discovery is still in flight).
  const project = state.projects.find((p) => p.id === projectId);
  const realId = project?.realId;
  const list = state[field] as SubEntityRow[];
  if (realId) {
    return list.filter((row) => row.projectId === realId || row.projectId === projectId) as SubEntityHost[K];
  }
  return list.filter((row) => row.projectId === projectId) as SubEntityHost[K];
}

// Auto-initialize on module load
initRepositories().catch(console.error);

// =============================================================================
// STORE STATE
// =============================================================================

interface ProjectsState {
  // View state
  activeViewKey: string;
  selectedProjectId: string | null;
  selectedTaskId: string | null;

  // Data (in-memory cache, source of truth is repository)
  projects: Project[];
  tasks: Task[];
  timeEntries: TimeEntry[];
  milestones: Milestone[];
  files: ProjectFile[];

  // UI
  sidebarMenu: SidebarMenu;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  initialized: boolean;
  syncStatus: Record<string, SyncStatus>; // projectId -> sync status

  // Actions - Initialization
  loadData: () => Promise<void>;
  loadFromServer: () => Promise<void>;

  // Actions - View
  setActiveViewKey: (key: string) => void;
  selectProject: (id: string | null) => void;
  selectTask: (id: string | null) => void;

  // Actions - Projects CRUD (async due to repository)
  createProject: (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'progress'>, userRole?: string) => Promise<string | { error: string }>;
  updateProject: (id: string, data: Partial<Project>, userRole?: string) => Promise<boolean | { error: string }>;
  deleteProject: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  getProject: (id: string) => Project | null;

  // Actions - Tasks CRUD (async)
  createTask: (projectId: string, data: Partial<Omit<Task, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>>, userRole?: string) => Promise<string | { error: string }>;
  updateTask: (id: string, data: Partial<Task>, userRole?: string) => Promise<boolean | { error: string }>;
  deleteTask: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  toggleTaskDone: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  renameTask: (id: string, title: string, userRole?: string) => Promise<boolean | { error: string }>;
  moveTaskStatus: (id: string, status: TaskStatus, userRole?: string) => Promise<boolean | { error: string }>;
  moveTaskPosition: (id: string, payload: { newParentId: string | null; beforeId?: string | null }, userRole?: string) => Promise<boolean | { error: string }>;
  getProjectTasks: (projectId: string) => Task[];
  getTask: (id: string) => Task | null;
  getTaskChildren: (parentId: string | null, projectId: string) => Task[];

  // Actions - Time Entries (async)
  createTimeEntry: (data: Omit<TimeEntry, 'id' | 'createdAt'>, userRole?: string) => Promise<string | { error: string }>;
  updateTimeEntry: (id: string, updates: Partial<TimeEntry>, userRole?: string) => Promise<boolean | { error: string }>;
  deleteTimeEntry: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  getProjectTimeEntries: (projectId: string) => TimeEntry[];
  getTaskTimeEntries: (taskId: string) => TimeEntry[];

  // Actions - Milestones (async)
  createMilestone: (projectId: string, title: string, dueDate: string, description?: string, linkedTaskIds?: string[], userRole?: string) => Promise<string | { error: string }>;
  updateMilestone: (id: string, updates: Partial<Pick<Milestone, 'title' | 'description' | 'dueDate' | 'completed' | 'completedAt' | 'involvedUsers' | 'linkedTaskIds'>>, userRole?: string) => Promise<boolean | { error: string }>;
  toggleMilestone: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  deleteMilestone: (id: string, userRole?: string) => Promise<boolean | { error: string }>;
  getProjectMilestones: (projectId: string) => Milestone[];

  // Actions - Files (async with Drive sync)
  createFile: (data: Omit<ProjectFile, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  deleteFile: (id: string, sessionId?: string) => Promise<boolean>;
  getProjectFiles: (projectId: string) => ProjectFile[];
  uploadFileToDrive: (
    sessionId: string,
    projectId: string,
    file: File,
    uploadedBy: string
  ) => Promise<ProjectFile | null>;

  // Actions - Drive Sync
  initProjectDriveFolder: (sessionId: string, projectId: string) => Promise<string | null>;
  syncProjectWithDrive: (sessionId: string, projectId: string, uploadedBy: string) => Promise<boolean>;
  getProjectSyncStatus: (projectId: string) => SyncStatus | null;

  // Actions - Dashboard
  getProjectDashboard: (projectId: string) => ProjectDashboard | null;
  updateProjectProgress: (id: string, progress: number) => Promise<boolean>;

  // Selectors
  getFilteredProjects: () => Project[];
  getProjectsByStatus: (status: ProjectStatus) => Project[];
  getActiveProjects: () => Project[];
  getProjectSummaries: () => ProjectSummary[];
  getUniqueClients: () => string[];
}

// =============================================================================
// HELPERS
// =============================================================================

function calculateRisk(project: Project): RiskLevel {
  // Archived projects are considered ok
  if (project.status === 'archiviato') return 'ok';

  // Health-based risk
  if (project.health === 'pericolo') return 'high';
  if (project.health === 'ritardo') return 'warn';

  // Check due date
  if (project.dueAt) {
    const dueDate = new Date(project.dueAt);
    const now = new Date();
    const daysLeft = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return 'high';
    if (daysLeft < 7 && project.progress < 80) return 'warn';
  }

  // Check budget
  if (project.budgetTotal && project.budgetUsed) {
    const budgetUsedPercent = (project.budgetUsed / project.budgetTotal) * 100;
    if (budgetUsedPercent > 90 && project.progress < 90) return 'warn';
    if (budgetUsedPercent > 100) return 'high';
  }

  return 'ok';
}

// =============================================================================
// STORE
// =============================================================================

export const useProjectsStore = create<ProjectsState>()(
  persist(
    (set, get) => ({
      // Initial state
      activeViewKey: 'all',
      selectedProjectId: null,
      selectedTaskId: null,
      projects: [],
      tasks: [],
      timeEntries: [],
      milestones: [],
      files: [],
      sidebarMenu: PROJECTS_SIDEBAR_MENU,
      status: 'idle',
      error: null,
      initialized: false,
      syncStatus: {},

      // Load data from repository (local)
      loadData: async () => {
        try {
          set({ status: 'loading' });

          // Load all data from repositories
          const [projects, tasks, timeEntries, milestones, files] = await Promise.all([
            projectsRepo.query<Project>({ scope: 'projects' }),
            tasksRepo.query<Task>({ scope: 'tasks' }),
            timeEntriesRepo.query<TimeEntry>({ scope: 'timeEntries' }),
            milestonesRepo.query<Milestone>({ scope: 'milestones' }),
            filesRepo.query<ProjectFile>({ scope: 'files' }),
          ]);

          set({
            projects,
            tasks,
            timeEntries,
            milestones,
            files,
            status: 'idle',
            initialized: true,
          });
        } catch (error) {
          console.error('Failed to load projects data:', error);
          set({ status: 'error', error: String(error) });
        }
      },

      // Load data from GraphQL server
      loadFromServer: async () => {
        const state = get();
        // Skip only if currently loading (avoid concurrent loads)
        if (state.status === 'loading') return;

        set({ status: 'loading', error: null });

        try {
          // Load projects from GraphQL
          const projects = await graphqlProjectsRepository.listProjects('');

          // TODO(issue:backend-id-serialization): the live backend returns
          // `id: ""` on the read side, so the mapper mints a deterministic
          // `synth-<slug>` for navigation. Sub-queries (tasks, milestones,
          // time entries) cannot resolve the synth- id on the backend, so
          // we use the real uuid discovered by `buildRealIdByTitle` when
          // available. Projects with duplicate titles may share the same
          // realId (the map keeps the last uuid per title) — acceptable
          // for the demo data, removed when the backend ships real ids.
          // Load tasks for each project
          const allTasks: Task[] = [];
          for (const project of projects) {
            try {
              const backendId = project.realId ?? project.id;
              const projectTasks = await graphqlProjectsRepository.listProjectTasks(backendId);
              allTasks.push(...projectTasks);
            } catch (e) {
              console.warn(`Failed to load tasks for project ${project.id}:`, e);
            }
          }

          // Load milestones for each project
          const allMilestones: Milestone[] = [];
          for (const project of projects) {
            try {
              const backendId = project.realId ?? project.id;
              const projectMilestones = await graphqlProjectsRepository.listProjectMilestones(backendId);
              allMilestones.push(...projectMilestones);
            } catch (e) {
              console.warn(`Failed to load milestones for project ${project.id}:`, e);
            }
          }

          // Load time entries for each project
          const allTimeEntries: TimeEntry[] = [];
          for (const project of projects) {
            try {
              const backendId = project.realId ?? project.id;
              const projectTimeEntries = await graphqlProjectsRepository.listProjectTimeEntries(backendId);
              allTimeEntries.push(...projectTimeEntries);
            } catch (e) {
              console.warn(`Failed to load time entries for project ${project.id}:`, e);
            }
          }

          set({
            projects,
            tasks: allTasks,
            milestones: allMilestones,
            timeEntries: allTimeEntries,
            files: [], // Files are loaded from Drive separately
            status: 'idle',
            initialized: true,
          });

          console.log(`[ProjectsStore] Loaded from GraphQL: ${projects.length} projects, ${allTasks.length} tasks, ${allMilestones.length} milestones`);

          // Demo seeding moved out of auto-init: it now requires an explicit
          // admin action (the "Seed demo data" button in ProjectsView), which
          // calls seedProjectsMockData() and writes through the GraphQL repo.
        } catch (error) {
          console.error('Failed to load from GraphQL:', error);
          set({
            status: 'error',
            error: error instanceof Error ? error.message : 'Failed to load data from server',
          });
        }
      },

      // View actions
      setActiveViewKey: (key) => set({ activeViewKey: key }),
      selectProject: (id) => set({ selectedProjectId: id, selectedTaskId: null }),
      selectTask: (id) => set({ selectedTaskId: id }),

      // Projects CRUD (async with repository)
      createProject: async (data) => {
        try {
          const project = await graphqlProjectsRepository.createProject('', data);
          set((state) => ({ projects: [project, ...state.projects] }));
          return project.id;
        } catch (error) {
          console.error('[ProjectsStore] createProject failed:', error);
          return { error: toErrorMessage(error) };
        }
      },

      updateProject: async (id, data) => {
        const project = get().projects.find((p) => p.id === id);
        if (!project) return false;
        try {
          const updated = await graphqlProjectsRepository.updateProject(id, {
            ...data,
            risk: calculateRisk({ ...project, ...data }),
          });
          set((state) => ({
            projects: state.projects.map((p) => (p.id === id ? updated : p)),
          }));
          return true;
        } catch (error) {
          console.error('[ProjectsStore] updateProject failed:', error);
          return { error: toErrorMessage(error) };
        }
      },

      deleteProject: async (id) => {
        if (!get().projects.some((p) => p.id === id)) return false;
        try {
          await graphqlProjectsRepository.deleteProject(id);
        } catch (error) {
          console.error('[ProjectsStore] deleteProject failed:', error);
          return { error: toErrorMessage(error) };
        }
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== id),
          tasks: state.tasks.filter((t) => t.projectId !== id),
          timeEntries: state.timeEntries.filter((t) => t.projectId !== id),
          milestones: state.milestones.filter((m) => m.projectId !== id),
          files: state.files.filter((f) => f.projectId !== id),
          selectedProjectId: state.selectedProjectId === id ? null : state.selectedProjectId,
        }));
        return true;
      },

      getProject: (id) => get().projects.find((p) => p.id === id) || null,

      // Tasks CRUD (async)
      createTask: async (projectId, data) => {
        // Append to the end of the siblings
        const siblings = get().tasks.filter(
          (t) => t.projectId === projectId && t.parentId === (data.parentId ?? null)
        );
        const maxOrder = siblings.reduce((max, t) => Math.max(max, t.order), -1);
        try {
          const task = await graphqlProjectsRepository.createTask('', projectId, {
            ...data,
            title: data.title || 'Nuovo task',
            status: data.status || 'todo',
            done: data.done ?? false,
            assignees: data.assignees || [],
            tags: data.tags || [],
            parentId: data.parentId ?? null,
            order: maxOrder + 1,
          } as Parameters<typeof graphqlProjectsRepository.createTask>[2]);
          set((state) => ({ tasks: [task, ...state.tasks] }));
          return task.id;
        } catch (error) {
          console.error('[ProjectsStore] createTask failed:', error);
          return { error: toErrorMessage(error) };
        }
      },

      updateTask: async (id, data) => {
        if (!get().tasks.some((t) => t.id === id)) return false;
        try {
          const updated = await graphqlProjectsRepository.updateTask(id, data);
          set((state) => ({
            tasks: state.tasks.map((t) => (t.id === id ? updated : t)),
          }));
          return true;
        } catch (error) {
          console.error('[ProjectsStore] updateTask failed:', error);
          return { error: toErrorMessage(error) };
        }
      },

      deleteTask: async (id) => {
        const state = get();
        const task = state.tasks.find((t) => t.id === id);
        if (!task) return false;

        // Collect all descendant IDs (recursive)
        const collectDescendants = (parentId: string): string[] => {
          const children = state.tasks.filter((t) => t.parentId === parentId);
          return children.flatMap((c) => [c.id, ...collectDescendants(c.id)]);
        };
        const descendantIds = collectDescendants(id);
        const allIdsToDelete = [id, ...descendantIds];

        // Children first, so the backend never holds an orphaned subtask
        try {
          for (const taskId of [...allIdsToDelete].reverse()) {
            await graphqlProjectsRepository.deleteTask(taskId);
          }
        } catch (error) {
          console.error('[ProjectsStore] deleteTask failed:', error);
          return { error: toErrorMessage(error) };
        }

        // Update in-memory state
        set((state) => ({
          tasks: state.tasks.filter((t) => !allIdsToDelete.includes(t.id)),
          timeEntries: state.timeEntries.filter((te) => !allIdsToDelete.includes(te.taskId || '')),
          selectedTaskId: allIdsToDelete.includes(state.selectedTaskId || '') ? null : state.selectedTaskId,
        }));

        return true;
      },

      toggleTaskDone: async (id) => {
        const task = get().tasks.find((t) => t.id === id);
        if (!task) return false;

        const newDone = !task.done;
        return await get().updateTask(id, {
          done: newDone,
          status: newDone ? 'done' : 'todo',
        });
      },

      renameTask: async (id, title) => {
        return await get().updateTask(id, { title: title.trim() || 'Senza titolo' });
      },

      moveTaskStatus: async (id, status) => {
        return await get().updateTask(id, { status, done: status === 'done' });
      },

      moveTaskPosition: async (id, payload) => {
        const state = get();
        const task = state.tasks.find((t) => t.id === id);
        if (!task) return false;

        const { newParentId, beforeId } = payload;

        // Get new siblings
        const siblings = state.tasks
          .filter((t) => t.projectId === task.projectId && t.parentId === newParentId && t.id !== id)
          .sort((a, b) => a.order - b.order);

        let newOrder: number;

        if (beforeId) {
          // Insert before specific sibling
          const beforeIndex = siblings.findIndex((s) => s.id === beforeId);
          if (beforeIndex === 0) {
            newOrder = siblings[0].order - 1;
          } else if (beforeIndex > 0) {
            newOrder = (siblings[beforeIndex - 1].order + siblings[beforeIndex].order) / 2;
          } else {
            // beforeId not found, append to end
            newOrder = siblings.length > 0 ? siblings[siblings.length - 1].order + 1 : 0;
          }
        } else {
          // Append to end
          newOrder = siblings.length > 0 ? siblings[siblings.length - 1].order + 1 : 0;
        }

        return await get().updateTask(id, { parentId: newParentId, order: newOrder });
      },

      getProjectTasks: (projectId) => {
        // TODO(issue:backend-id-serialization): the read side of the
        // backend returns `id: ""` for projects, so the mapper mints a
        // `synth-<slug>` for navigation. Sub-entities (tasks, milestones,
        // time entries) are queried with the project's realId, so their
        // `projectId` field is the real uuid. The caller here usually
        // holds the synth- id (from the URL), so the simple equality
        // check would miss everything. Match by the project's realId
        // when we know it; fall back to the input for cold paths where
        // the project hasn't been resolved yet.
        return resolveProjectSubEntities(get(), projectId, 'tasks');
      },

      getTask: (id) => {
        return get().tasks.find((t) => t.id === id) || null;
      },

      getTaskChildren: (parentId, projectId) => {
        return get()
          .tasks.filter((t) => t.projectId === projectId && t.parentId === parentId)
          .sort((a, b) => a.order - b.order);
      },

      // Time Entries (async)
      createTimeEntry: async (data) => {
        const id = genId('tme');
        const entry: TimeEntry = {
          ...data,
          id,
          createdAt: new Date().toISOString(),
        };

        // Persist to repository
        await timeEntriesRepo.put(entry);

        // Update in-memory state
        set((state) => ({
          timeEntries: [entry, ...state.timeEntries],
        }));

        return id;
      },

      updateTimeEntry: async (id, updates) => {
        const state = get();
        const existing = state.timeEntries.find((t) => t.id === id);
        if (!existing) return false;

        const updated: TimeEntry = {
          ...existing,
          ...updates,
        };

        // Update in repository
        await timeEntriesRepo.put(updated);

        // Update in-memory state
        set((state) => ({
          timeEntries: state.timeEntries.map((t) => (t.id === id ? updated : t)),
        }));

        return true;
      },

      deleteTimeEntry: async (id) => {
        const state = get();
        if (!state.timeEntries.some((t) => t.id === id)) return false;

        // Delete from repository
        await timeEntriesRepo.delete(id);

        // Update in-memory state
        set((state) => ({
          timeEntries: state.timeEntries.filter((t) => t.id !== id),
        }));

        return true;
      },

      getProjectTimeEntries: (projectId) => {
        return resolveProjectSubEntities(get(), projectId, 'timeEntries');
      },

      getTaskTimeEntries: (taskId) => {
        return get().timeEntries.filter((t) => t.taskId === taskId);
      },

      // Milestones (async)
      createMilestone: async (projectId, title, dueDate, description, linkedTaskIds) => {
        const id = genId('mst');
        const now = new Date().toISOString();
        const milestone: Milestone = {
          id,
          projectId,
          title,
          description,
          dueDate,
          completed: false,
          linkedTaskIds,
          createdAt: now,
          updatedAt: now,
        };

        // Persist to repository
        await milestonesRepo.put(milestone);

        // Update in-memory state
        set((state) => ({
          milestones: [...state.milestones, milestone],
        }));

        return id;
      },

      updateMilestone: async (id, updates) => {
        const state = get();
        const milestone = state.milestones.find((m) => m.id === id);
        if (!milestone) return false;

        const updatedMilestone: Milestone = {
          ...milestone,
          ...updates,
          updatedAt: new Date().toISOString(),
        };

        // Persist to repository
        await milestonesRepo.put(updatedMilestone);

        // Update in-memory state
        set((state) => ({
          milestones: state.milestones.map((m) => (m.id === id ? updatedMilestone : m)),
        }));

        return true;
      },

      toggleMilestone: async (id) => {
        const state = get();
        const milestone = state.milestones.find((m) => m.id === id);
        if (!milestone) return false;

        const updatedMilestone = {
          ...milestone,
          completed: !milestone.completed,
          completedAt: !milestone.completed ? new Date().toISOString() : undefined,
        };

        // Persist to repository
        await milestonesRepo.put(updatedMilestone);

        // Update in-memory state
        set((state) => ({
          milestones: state.milestones.map((m) => (m.id === id ? updatedMilestone : m)),
        }));

        return true;
      },

      deleteMilestone: async (id) => {
        const state = get();
        if (!state.milestones.some((m) => m.id === id)) return false;

        // Delete from repository
        await milestonesRepo.delete(id);

        // Update in-memory state
        set((state) => ({
          milestones: state.milestones.filter((m) => m.id !== id),
        }));

        return true;
      },

      getProjectMilestones: (projectId) => {
        return resolveProjectSubEntities(get(), projectId, 'milestones');
      },

      // Files CRUD (async with Drive sync)
      createFile: async (data) => {
        const now = new Date().toISOString();
        const id = genId('fil');
        const file: ProjectFile = {
          ...data,
          id,
          createdAt: now,
          updatedAt: now,
        };

        // Persist to repository
        await filesRepo.put(file);

        // Update in-memory state
        set((state) => ({
          files: [file, ...state.files],
        }));

        return id;
      },

      deleteFile: async (id, sessionId) => {
        const state = get();
        const file = state.files.find((f) => f.id === id);
        if (!file) return false;

        // If connected to Drive and has externalId, delete from Drive too
        if (sessionId && file.externalId) {
          try {
            await deleteFromDrive(sessionId, file.externalId);
          } catch (error) {
            console.warn('Failed to delete file from Drive:', error);
          }
        }

        // Delete from repository
        await filesRepo.delete(id);

        // Update in-memory state
        set((state) => ({
          files: state.files.filter((f) => f.id !== id),
        }));

        return true;
      },

      getProjectFiles: (projectId) => {
        return get().files.filter((f) => f.projectId === projectId);
      },

      uploadFileToDrive: async (sessionId, projectId, file, uploadedBy) => {
        const state = get();
        const project = state.projects.find((p) => p.id === projectId);
        if (!project) return null;

        try {
          // Ensure project has a Drive folder
          let folderId = project.driveFolderId;
          if (!folderId) {
            folderId = await get().initProjectDriveFolder(sessionId, projectId);
            if (!folderId) return null;
          }

          // Upload file to Drive
          const driveFile = await uploadDriveFile(sessionId, file, folderId);

          // Create local ProjectFile record
          const projectFile: ProjectFile = {
            id: genId('fil'),
            projectId,
            name: driveFile.name,
            mimeType: driveFile.mimeType,
            size: parseInt(driveFile.size || '0', 10),
            provider: 'google-drive',
            externalUrl: driveFile.webViewLink || `https://drive.google.com/file/d/${driveFile.id}/view`,
            externalId: driveFile.id,
            thumbnailUrl: driveFile.thumbnailLink,
            uploadedBy,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          // Persist to repository
          await filesRepo.put(projectFile);

          // Update in-memory state
          set((state) => ({
            files: [projectFile, ...state.files],
          }));

          return projectFile;
        } catch (error) {
          console.error('Failed to upload file to Drive:', error);
          return null;
        }
      },

      // Drive Sync actions
      initProjectDriveFolder: async (sessionId, projectId) => {
        const state = get();
        const project = state.projects.find((p) => p.id === projectId);
        if (!project) return null;

        // Already has a folder
        if (project.driveFolderId) {
          return project.driveFolderId;
        }

        try {
          const { folderId } = await getOrCreateProjectFolder(
            sessionId,
            projectId,
            project.title,
            project.driveFolderId
          );

          // Update project with folder ID
          await get().updateProject(projectId, { driveFolderId: folderId });

          return folderId;
        } catch (error) {
          console.error('Failed to create project Drive folder:', error);
          return null;
        }
      },

      syncProjectWithDrive: async (sessionId, projectId, uploadedBy) => {
        const state = get();
        const project = state.projects.find((p) => p.id === projectId);
        if (!project || !project.driveFolderId) return false;

        try {
          const localFiles = state.files.filter((f) => f.projectId === projectId);

          // Sync from Drive
          const { newFiles, deletedFileIds } = await syncFromDrive(
            sessionId,
            project.driveFolderId,
            projectId,
            localFiles,
            uploadedBy
          );

          // Add new files from Drive
          for (const file of newFiles) {
            await filesRepo.put(file);
          }

          // Remove files that were deleted from Drive
          for (const fileId of deletedFileIds) {
            await filesRepo.delete(fileId);
          }

          // Update project sync timestamp
          const now = new Date().toISOString();
          await get().updateProject(projectId, { driveLastSyncAt: now });

          // Update in-memory state
          set((state) => ({
            files: [
              ...newFiles,
              ...state.files.filter((f) =>
                f.projectId !== projectId || !deletedFileIds.includes(f.id)
              ),
            ],
            syncStatus: {
              ...state.syncStatus,
              [projectId]: {
                isSynced: true,
                lastSyncAt: now,
                localCount: localFiles.length + newFiles.length - deletedFileIds.length,
                driveCount: localFiles.length + newFiles.length - deletedFileIds.length,
                pendingUploads: 0,
                pendingDeletes: 0,
              },
            },
          }));

          return true;
        } catch (error) {
          console.error('Failed to sync project with Drive:', error);
          return false;
        }
      },

      getProjectSyncStatus: (projectId) => {
        return get().syncStatus[projectId] || null;
      },

      // Dashboard
      getProjectDashboard: (projectId) => {
        const state = get();
        const project = state.projects.find((p) => p.id === projectId);
        if (!project) return null;

        const tasks = state.tasks.filter((t) => t.projectId === projectId);
        const timeEntries = state.timeEntries.filter((t) => t.projectId === projectId);
        const milestones = state.milestones.filter((m) => m.projectId === projectId);

        const alerts: string[] = [];

        // Check overdue tasks
        const overdueTasks = tasks.filter((t) => t.dueAt && new Date(t.dueAt) < new Date() && t.status !== 'done');
        if (overdueTasks.length > 0) {
          alerts.push(`${overdueTasks.length} task scaduti`);
        }

        // Check overdue milestones
        const overdueMilestones = milestones.filter((m) => !m.completed && new Date(m.dueDate) < new Date());
        if (overdueMilestones.length > 0) {
          alerts.push(`${overdueMilestones.length} milestone scadute`);
        }

        // Calculate budget used from time entries
        const budgetUsed = timeEntries.reduce((sum, te) => sum + (te.minutes / 60) * (te.costRate / 100), 0);

        return {
          projectId,
          status: project.status,
          health: project.health,
          progressPercent: project.progress,
          dueAt: project.dueAt,
          budgetTotal: project.budgetTotal,
          budgetUsed: Math.round(budgetUsed * 100), // Convert to cents
          risk: calculateRisk(project),
          alerts,
        };
      },

      updateProjectProgress: async (id, progress) => {
        const clamped = Math.max(0, Math.min(100, progress));
        return await get().updateProject(id, { progress: clamped });
      },

      // Selectors
      getFilteredProjects: () => {
        const state = get();
        const key = state.activeViewKey;

        // Client filter (key format: "client:ClientName")
        if (key.startsWith('client:')) {
          const clientName = key.substring(7); // Remove "client:" prefix
          return state.projects.filter((p) => p.clientName === clientName && p.status === 'attivo');
        }

        switch (key) {
          // Status filters
          case 'attivo':
            return state.projects.filter((p) => p.status === 'attivo');
          case 'archiviato':
            return state.projects.filter((p) => p.status === 'archiviato');
          // Health filters
          case 'da-iniziare':
            return state.projects.filter((p) => p.health === 'da-iniziare' && p.status === 'attivo');
          case 'in-corso':
            return state.projects.filter((p) => p.health === 'in-corso' && p.status === 'attivo');
          case 'pericolo':
            return state.projects.filter((p) => p.health === 'pericolo' && p.status === 'attivo');
          case 'ritardo':
            return state.projects.filter((p) => p.health === 'ritardo' && p.status === 'attivo');
          // Priority filters
          case 'urgent':
            return state.projects.filter((p) => p.priority === 'urgente' && p.status === 'attivo');
          case 'high':
            return state.projects.filter((p) => (p.priority === 'alta' || p.priority === 'urgente') && p.status === 'attivo');
          // Default: all active projects
          default:
            return state.projects.filter((p) => p.status === 'attivo');
        }
      },

      getProjectsByStatus: (status) => {
        return get().projects.filter((p) => p.status === status);
      },

      getActiveProjects: () => {
        return get().projects.filter((p) => p.status === 'attivo');
      },

      getProjectSummaries: (): ProjectSummary[] => {
        return get().projects.map((p) => ({
          id: p.id,
          title: p.title,
          createdAt: p.createdAt,
          dueAt: p.dueAt,
          budgetTotal: p.budgetTotal,
          budgetUsed: p.budgetUsed,
          risk: p.risk,
          favorite: p.favorite,
          clientId: p.clientId,
          clientName: p.clientName,
          status: p.status,
          health: p.health,
        }));
      },

      getUniqueClients: (): string[] => {
        const activeProjects = get().projects.filter((p) => p.status === 'attivo');
        const clientNames = activeProjects
          .map((p) => p.clientName)
          .filter((name): name is string => !!name);
        return [...new Set(clientNames)].sort();
      },
    }),
    {
      name: 'projects-store',
      storage: createZustandStorage(),
      // Only persist UI state, not data (data goes to repository)
      partialize: (state) => ({
        activeViewKey: state.activeViewKey,
        selectedProjectId: state.selectedProjectId,
        selectedTaskId: state.selectedTaskId,
      }),
    }
  )
);

// =============================================================================
// SEED MOCK DATA (via GraphQL — persisted to backend)
// =============================================================================

// Flag to prevent concurrent seeding
let isSeeding = false;

export async function seedProjectsMockData() {
  const store = useProjectsStore.getState();

  // Prevent concurrent seeding and double-seeding
  if (isSeeding || store.projects.length > 0) return;
  isSeeding = true;

  const repo = graphqlProjectsRepository;

  try {
    console.log('[SeedData] Seeding demo projects to GraphQL...');

    // Create projects via GraphQL (persisted to backend)
    const p1 = await repo.createProject('', {
      title: 'Sito Web — Studio Lumen',
      description: 'Redesign completo del sito web aziendale con nuovo CMS',
      status: 'attivo',
      health: 'in-corso',
      priority: 'alta',
      startDate: '2024-01-15',
      dueAt: '2024-03-30',
      budgetTotal: 1200000,
      budgetUsed: 450000,
      team: [{ userId: 'usr-1', role: 'Project Manager', displayName: 'Mario Rossi' }],
      tags: ['web', 'design', 'cms'],
      clientName: 'Studio Lumen',
    } as any);

    const p2 = await repo.createProject('', {
      title: 'Convention 10 anni',
      description: 'Organizzazione evento celebrativo anniversario azienda',
      status: 'attivo',
      health: 'pericolo',
      priority: 'urgente',
      startDate: '2024-02-01',
      dueAt: '2024-02-28',
      budgetTotal: 2500000,
      budgetUsed: 1800000,
      team: [
        { userId: 'usr-2', role: 'Event Manager', displayName: 'Elena Bianchi' },
        { userId: 'usr-3', role: 'Designer', displayName: 'Luca Verdi' },
      ],
      tags: ['evento', 'marketing'],
      clientName: 'MegaCorp SpA',
    } as any);

    const p3 = await repo.createProject('', {
      title: 'CRM Leggero — Studio Notaio',
      description: 'Sistema CRM personalizzato per gestione clienti studio notarile',
      status: 'attivo',
      health: 'da-iniziare',
      priority: 'media',
      startDate: '2024-03-01',
      dueAt: '2024-05-15',
      budgetTotal: 800000,
      team: [],
      tags: ['crm', 'software', 'legal'],
      clientName: 'Studio Notaio Bianchi',
    } as any);

    // Create tasks for prj1 - with hierarchy
    const tsk1 = await repo.createTask('', p1.id, {
      title: 'Fase 1: Design',
      status: 'doing',
      done: false,
      priority: 'alta',
      assignees: ['usr-1'],
      tags: ['design'],
    } as any);

    await repo.createTask('', p1.id, {
      title: 'Wireframe homepage',
      status: 'done',
      done: true,
      priority: 'alta',
      parentId: tsk1.id,
      assignees: ['usr-1'],
      tags: ['design'],
    } as any);
    await repo.createTask('', p1.id, {
      title: 'Design UI sistema',
      status: 'doing',
      done: false,
      priority: 'alta',
      parentId: tsk1.id,
      assignees: ['usr-1'],
      tags: ['design'],
    } as any);
    await repo.createTask('', p1.id, {
      title: 'Design componenti',
      status: 'todo',
      done: false,
      priority: 'media',
      parentId: tsk1.id,
      assignees: [],
      tags: ['design'],
    } as any);

    const tsk2 = await repo.createTask('', p1.id, {
      title: 'Fase 2: Sviluppo',
      status: 'todo',
      done: false,
      priority: 'media',
      assignees: [],
      tags: ['dev'],
    } as any);

    await repo.createTask('', p1.id, {
      title: 'Sviluppo frontend React',
      status: 'todo',
      done: false,
      priority: 'media',
      parentId: tsk2.id,
      dueAt: '2024-03-15',
      assignees: [],
      tags: ['dev', 'react'],
    } as any);
    await repo.createTask('', p1.id, {
      title: 'Integrazione CMS',
      status: 'todo',
      done: false,
      priority: 'media',
      parentId: tsk2.id,
      dueAt: '2024-03-25',
      assignees: [],
      tags: ['dev', 'cms'],
    } as any);

    // Create tasks for prj2 - with hierarchy
    const tsk3 = await repo.createTask('', p2.id, {
      title: 'Logistica',
      status: 'doing',
      done: false,
      priority: 'urgente',
      assignees: ['usr-2'],
      tags: ['logistics'],
    } as any);

    await repo.createTask('', p2.id, {
      title: 'Prenotazione location',
      status: 'done',
      done: true,
      priority: 'urgente',
      parentId: tsk3.id,
      assignees: ['usr-2'],
      tags: ['logistics'],
    } as any);
    await repo.createTask('', p2.id, {
      title: 'Conferma catering',
      status: 'doing',
      done: false,
      priority: 'alta',
      parentId: tsk3.id,
      dueAt: '2024-02-20',
      assignees: ['usr-2'],
      tags: ['logistics'],
    } as any);

    await repo.createTask('', p2.id, {
      title: 'Materiali grafici',
      status: 'todo',
      done: false,
      priority: 'alta',
      dueAt: '2024-02-22',
      assignees: ['usr-3'],
      tags: ['design'],
    } as any);

    // Create milestones
    await repo.createMilestone('', p1.id, 'Approvazione design', '2024-02-15');
    await repo.createMilestone('', p1.id, 'Go live staging', '2024-03-15');
    await repo.createMilestone('', p1.id, 'Lancio produzione', '2024-03-30');

    await repo.createMilestone('', p2.id, 'Conferma fornitori', '2024-02-10');
    await repo.createMilestone('', p2.id, 'Prova generale', '2024-02-25');

    // Create time entries
    await repo.createTimeEntry('', p1.id, {
      userId: 'usr-1',
      userName: 'Mario Rossi',
      minutes: 240,
      costRate: 5000,
      billableRate: 8000,
      billable: true,
      description: 'Analisi requisiti e wireframe',
      at: '2024-01-20',
    } as any);
    await repo.createTimeEntry('', p1.id, {
      userId: 'usr-1',
      userName: 'Mario Rossi',
      minutes: 180,
      costRate: 5000,
      billableRate: 8000,
      billable: true,
      description: 'Design homepage',
      at: '2024-01-22',
    } as any);

    console.log(`[SeedData] Demo data seeded: 3 projects, tasks, milestones, time entries`);

    // Reload from server to get everything in the store consistently
    await store.loadFromServer();
  } catch (err) {
    console.error('[SeedData] Failed to seed demo data:', err);
  } finally {
    isSeeding = false;
  }
}
